import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { types } from '@boss/boss-client';
import type {
  DeliveryMethod,
  IBasket,
  StockAvailability,
  StockStatusLabelTranslations,
  StockStatusLine,
  StockStatusSummary,
} from '@boss/services/client';
import { IBasketLine, IBasketPatchLine } from '@boss/services/client';
import type { IArticleStock } from '@boss/types/b2b-b2c';

import { useAccount, usePointsOfSales } from '../../client-queries';
import { useStockByWarehouseIds } from '../../client-queries/stock';
import { storeConfig } from '../../utils/options';
import { handleStores } from '../../utils/store';

export interface ShopSelection {
  id: string;
  coloradepotid: string;
  name: string;
}

export interface UseBasketStockArgs {
  locale: string;
  basketLines: IBasketLine[];
  /** Raw basket (GET response) — the hook derives the persisted choice + loaded flags from it. */
  basket: IBasket | undefined;
  /** For persisting delivery method to the basket API (Issue 3). */
  basketId?: string;
  /** PATCH-based delivery persistence (`usePatchBasket` mutateAsync) — wrapped storeid op per backend confirmation. */
  patchBasket?: (params: { basketId: string; patchLines: IBasketPatchLine[] }) => Promise<IBasket | undefined | null>;
}

export interface UseBasketStockResult {
  deliveryMethod: DeliveryMethod;
  setDeliveryMethod: (method: DeliveryMethod) => void;
  selectedShop: ShopSelection | undefined;
  setSelectedShop: (shop: ShopSelection | undefined) => void;
  /** True when Pickup is selected without a store — gates the continue/checkout actions. */
  isPickupWithoutShop: boolean;
  /** Pickup-capable stores rendered in the dropdown. */
  stores: types.Store[];
  /** The account's favourite shop id (raw preference value) for the favourite badge. */
  favouriteShopId: string | undefined;
  /** Independent stock status for both methods; null means no reliable data/warehouse. */
  methodSummaries: { Delivery: StockStatusSummary; Pickup: StockStatusSummary };
  isStockLoading: boolean;
  /** Selected-warehouse line labels, keyed by stockproductnumber. */
  stockBySkuid: Map<string, Pick<StockStatusLine, 'status' | 'label'>>;
  /** Per-store dropdown dots from the same multiwarehouse response. */
  storeStockMap: Map<string, 'green' | 'red'>;
  isStoreStockLoading: boolean;
  /** True only when the current basket's latest delivery write is confirmed by its PATCH response. */
  waitForDeliveryPersistence: () => Promise<boolean>;
}

/**
 * [46232] sv05 basket levermethode + stock hook.
 * Manages page-scoped deliveryMethod/selectedShop state (no Account API write).
 * Builds skus from basketLines AS-IS and uses one multiwarehouse response for
 * method summaries, selected-warehouse line labels, and per-store dropdown dots.
 * Falls back to 'Delivery' when account preference is unavailable or invalid.
 *
 * All stock-derived fields are live getters: they read from the React Query
 * result on each access so values update as queries resolve (fixes Issue 4).
 * When basketId + patchBasket are provided, changes to delivery method or shop
 * are persisted to the basket API (Issue 3).
 */

type StockSku = { skuid: string; quantity?: number; unit?: string };

const mapAvailability = (
  availability: StockAvailability | undefined,
  translations: StockStatusLabelTranslations,
): { status: StockStatusLine['status']; label: string } => {
  if (availability === 'High Stock') {
    return { status: 'available', label: translations.available };
  }

  if (availability === 'Low Stock') {
    return { status: 'insufficient', label: translations.insufficient };
  }

  return { status: 'unavailable', label: translations.unavailable };
};

/** One multiwarehouse response → one SKU-status list per warehouse. Empty/error warehouses stay unknown. */
const buildWarehouseStockLines = (
  response: IArticleStock | undefined,
  warehouseIds: string[],
  skus: StockSku[],
  translations: StockStatusLabelTranslations,
): Map<string, StockStatusLine[]> => {
  const byWarehouse = new Map<string, StockStatusLine[]>();

  if (!response) {
    return byWarehouse;
  }

  for (const warehouse of warehouseIds) {
    const responseLines = response.filter(line => line.warehouse === warehouse);

    if (responseLines.length === 0) {
      continue;
    }

    byWarehouse.set(
      warehouse,
      skus.map(({ skuid }) => {
        const stockLine = responseLines.find(line => line.id === skuid);
        const { status, label } = mapAvailability(stockLine?.availability, translations);

        return { skuid, warehouse: stockLine?.warehouse ?? null, status, label };
      }),
    );
  }

  return byWarehouse;
};

const summarizeWarehouse = (lines: StockStatusLine[] | undefined): StockStatusSummary => {
  if (!lines?.length) {
    return null;
  }

  return lines.some(line => line.status !== 'available') ? 'red' : 'green';
};

const buildStoreStockMap = (
  storeWarehouseIds: string[],
  stockLinesByWarehouse: Map<string, StockStatusLine[]>,
): Map<string, 'green' | 'red'> => {
  const storeStockMap = new Map<string, 'green' | 'red'>();

  for (const warehouse of storeWarehouseIds) {
    const summary = summarizeWarehouse(stockLinesByWarehouse.get(warehouse));

    if (summary) {
      storeStockMap.set(warehouse, summary);
    }
  }

  return storeStockMap;
};

const buildStockBySkuid = (lines: StockStatusLine[] | undefined) => {
  const stockBySkuid = new Map<string, Pick<StockStatusLine, 'status' | 'label'>>();

  for (const line of lines ?? []) {
    stockBySkuid.set(line.skuid, { status: line.status, label: line.label });
  }

  return stockBySkuid;
};

const getMethodSummaries = (
  stockLinesByWarehouse: Map<string, StockStatusLine[]>,
  selectedShop: ShopSelection | undefined,
): { Delivery: StockStatusSummary; Pickup: StockStatusSummary } => ({
  Delivery: summarizeWarehouse(stockLinesByWarehouse.get('HM')),
  Pickup: selectedShop ? summarizeWarehouse(stockLinesByWarehouse.get(selectedShop.coloradepotid)) : null,
});

const getSelectedWarehouse = (deliveryMethod: DeliveryMethod, selectedShop: ShopSelection | undefined) =>
  deliveryMethod === 'Delivery' ? 'HM' : selectedShop?.coloradepotid;

const hasPickupWithoutShop = (deliveryMethod: DeliveryMethod, selectedShop: ShopSelection | undefined) =>
  deliveryMethod === 'Pickup' && !selectedShop;

type ResolvedPrefill = { method?: DeliveryMethod; shop?: ShopSelection; persistShop?: boolean };

type PrefillArgs = {
  persistedDeliveryMethod: number | undefined;
  persistedShop: ShopSelection | null;
  selectedShop: ShopSelection | undefined;
  storesLoaded: boolean;
  basketLoaded: boolean;
  preferredShop: ShopSelection | null;
  preferredDelivery: DeliveryMethod | null | undefined;
};

const getPreferredDeliveryMethod = (
  preferredDelivery: DeliveryMethod | null | undefined,
  preferredShop: ShopSelection | null | undefined,
): DeliveryMethod => (preferredDelivery === 'Pickup' && !preferredShop ? 'Delivery' : preferredDelivery ?? 'Delivery');

const resolvePersistedPrefill = ({
  persistedDeliveryMethod,
  persistedShop,
  storesLoaded,
  basketLoaded,
  preferredShop,
}: PrefillArgs): ResolvedPrefill => {
  const method: DeliveryMethod = persistedDeliveryMethod === 3 ? 'Pickup' : 'Delivery';

  if (persistedShop) {
    return { method, shop: persistedShop };
  }

  if (!storesLoaded) {
    return { method };
  }

  if (basketLoaded && preferredShop) {
    return { method, shop: preferredShop, persistShop: true };
  }

  return { method };
};

const resolvePreferredPrefill = ({
  basketLoaded,
  selectedShop,
  preferredShop,
  preferredDelivery,
}: PrefillArgs): ResolvedPrefill => {
  const method = getPreferredDeliveryMethod(preferredDelivery, preferredShop);

  if (basketLoaded && !selectedShop && preferredShop) {
    return { method, shop: preferredShop, persistShop: true };
  }

  return { method };
};

const resolveBasketPrefill = (args: PrefillArgs): ResolvedPrefill | undefined =>
  hasPersistedChoice(args.persistedDeliveryMethod) ? resolvePersistedPrefill(args) : resolvePreferredPrefill(args);

const applyResolvedPrefill = (
  prefill: ResolvedPrefill,
  selectedShop: ShopSelection | undefined,
  setDeliveryMethod: (method: DeliveryMethod) => void,
  setSelectedShop: (shop: ShopSelection) => void,
): void => {
  if (prefill.method) {
    setDeliveryMethod(prefill.method);
  }

  if (prefill.shop && prefill.shop.id !== selectedShop?.id) {
    setSelectedShop(prefill.shop);
  }
};

const persistResolvedPrefillShop = (
  prefill: ResolvedPrefill,
  currentDeliveryMethod: DeliveryMethod,
  persistDelivery: (method: DeliveryMethod, shop: ShopSelection | undefined) => void,
): void => {
  if (prefill.persistShop && prefill.shop) {
    persistDelivery(prefill.method ?? currentDeliveryMethod, prefill.shop);
  }
};

const applyBasketPrefill = ({
  userTouched,
  prefill,
  selectedShop,
  setDeliveryMethod,
  setSelectedShop,
  currentDeliveryMethod,
  persistDelivery,
}: {
  userTouched: boolean;
  prefill: ResolvedPrefill | undefined;
  selectedShop: ShopSelection | undefined;
  setDeliveryMethod: (method: DeliveryMethod) => void;
  setSelectedShop: (shop: ShopSelection) => void;
  currentDeliveryMethod: DeliveryMethod;
  persistDelivery: (method: DeliveryMethod, shop: ShopSelection | undefined) => void;
}) => {
  if (userTouched || !prefill) {
    return;
  }

  applyResolvedPrefill(prefill, selectedShop, setDeliveryMethod, setSelectedShop);
  persistResolvedPrefillShop(prefill, currentDeliveryMethod, persistDelivery);
};

const mapDeliveryMethod = (method: DeliveryMethod): 1 | 3 => (method === 'Pickup' ? 3 : 1);

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

const readReturnedDeliveryMethod = (delivery: Record<string, unknown>): unknown => {
  const rawMethod = delivery.deliverymethod;

  if (typeof rawMethod === 'number') {
    return rawMethod;
  }

  if (isRecord(rawMethod)) {
    return rawMethod.deliverymethod;
  }

  return undefined;
};

const confirmsBasketDelivery = (
  response: unknown,
  basketId: string,
  method: DeliveryMethod,
  shop: ShopSelection | undefined,
): boolean => {
  if (!isRecord(response)) {
    return false;
  }

  if (response.id !== basketId || !isRecord(response.delivery)) {
    return false;
  }

  if (readReturnedDeliveryMethod(response.delivery) !== mapDeliveryMethod(method)) {
    return false;
  }

  if (method !== 'Pickup') {
    return true;
  }

  return !!shop && response.delivery.storeid === shop.id;
};

type DeliveryWriteQueue = { tail: Promise<void>; lastSucceeded: boolean };
type BasketSession = { basketId?: string; generation: number; lastBasketId?: string };

const shouldResetBasketSelection = (session: BasketSession, basketId: string | undefined): boolean => {
  if (session.basketId === basketId) {
    return false;
  }

  const previousBasketId = session.lastBasketId;

  session.basketId = basketId;
  session.generation += 1;

  if (basketId) {
    session.lastBasketId = basketId;
  }

  return Boolean(previousBasketId && basketId && previousBasketId !== basketId);
};

const persistBasketDelivery = (
  queues: Map<string, DeliveryWriteQueue>,
  basketId: string | undefined,
  patchBasket: UseBasketStockArgs['patchBasket'],
  method: DeliveryMethod,
  shop: ShopSelection | undefined,
) => {
  if (!basketId || !patchBasket) {
    return;
  }

  let queue = queues.get(basketId);

  if (!queue) {
    queue = { tail: Promise.resolve(), lastSucceeded: true };
    queues.set(basketId, queue);
  }

  // Persistence transport per backend confirmation: the previously-ignored wrapped /delivery/storeid
  // PATCH op is fixed and applied again. The storeid op is sent only for Pickup with a store; the
  // Delivery switch omits it entirely (no empty string, no echo, no warehouse op).
  const patchLines: IBasketPatchLine[] = [
    {
      path: '/delivery/deliverymethod',
      op: 'replace',
      value: { deliverymethod: mapDeliveryMethod(method) },
    },
  ];

  if (method === 'Pickup' && shop) {
    patchLines.push({ path: '/delivery/storeid', op: 'replace', value: { storeid: shop.id } });
  }

  const currentQueue = queue;
  const write = currentQueue.tail.then(() => patchBasket({ basketId, patchLines }));

  // usePatchBasket already toast-reports failures (and rethrows); keep the queue fulfilled so later writes still run.
  currentQueue.tail = write.then(
    response => {
      currentQueue.lastSucceeded = confirmsBasketDelivery(response, basketId, method, shop);
    },
    () => {
      currentQueue.lastSucceeded = false;
    },
  );
};

const awaitBasketDeliveryWrites = async (
  queues: Map<string, DeliveryWriteQueue>,
  basketId: string | undefined,
): Promise<boolean> => {
  if (!basketId) {
    return true;
  }

  const queue = queues.get(basketId);

  if (!queue) {
    return true;
  }

  let latestTail: Promise<void>;

  do {
    latestTail = queue.tail;
    await latestTail;
  } while (latestTail !== queue.tail);

  return queue.lastSucceeded;
};

const waitForBasketDeliveryPersistence = async (
  queues: Map<string, DeliveryWriteQueue>,
  basketId: string | undefined,
  patchBasket: UseBasketStockArgs['patchBasket'],
  hasPendingSelection: boolean,
  session: BasketSession,
  generation: number,
): Promise<boolean> => {
  if (!basketId || !patchBasket || hasPendingSelection) {
    return false;
  }

  const persisted = await awaitBasketDeliveryWrites(queues, basketId);

  return persisted && session.basketId === basketId && session.generation === generation;
};

const persistPendingBasketSelection = (
  basketId: string | undefined,
  patchBasket: UseBasketStockArgs['patchBasket'],
  hasPendingSelection: boolean,
  persist: () => void,
) => {
  if (basketId && patchBasket && hasPendingSelection) {
    persist();
  }
};

/** Priority: basket-persisted choice (3 → Pickup, 1/2 → Delivery) > account favourite > 'Delivery'. */
const getInitialDeliveryMethod = (
  persistedDeliveryMethod: number | null | undefined,
  preferredDelivery: DeliveryMethod | null | undefined,
  preferredShop: ShopSelection | null | undefined,
): DeliveryMethod => {
  if (persistedDeliveryMethod === 3) {
    return 'Pickup';
  }

  if (persistedDeliveryMethod === 1 || persistedDeliveryMethod === 2) {
    return 'Delivery';
  }

  return getPreferredDeliveryMethod(preferredDelivery, preferredShop);
};

/** A numeric basket-persisted method means the basket owns the selection (1 = delivery, 2 = wharf, 3 = pickup). */
const hasPersistedChoice = (persistedDeliveryMethod: number | null | undefined): boolean =>
  persistedDeliveryMethod === 1 || persistedDeliveryMethod === 2 || persistedDeliveryMethod === 3;

/** Shop priority: basket-persisted store > account favourite — a persisted choice never seeds from the favourite. */
const getInitialShop = (
  persistedShop: ShopSelection | null | undefined,
  persistedDeliveryMethod: number | null | undefined,
  preferredShop: ShopSelection | null | undefined,
): ShopSelection | undefined => {
  if (persistedShop) {
    return persistedShop;
  }

  // A persisted basket choice owns the selection: wait for its store instead of the account favourite.
  return hasPersistedChoice(persistedDeliveryMethod) ? undefined : preferredShop ?? undefined;
};

/** Defensive read: `delivery.deliverymethod` is a plain number (old patches wrote a nested object). */
const readPersistedDeliveryMethod = (basket: IBasket | undefined): number | undefined => {
  const raw = basket?.delivery?.deliverymethod as number | { deliverymethod?: number } | undefined;

  return typeof raw === 'number' ? raw : raw?.deliverymethod;
};

/** storeid is canonical; warehouse (coloradepotid) is the fallback. Ids are strings — coerce defensively. */
const resolvePersistedShop = (
  stores: types.Store[],
  persistedStoreId: string | undefined,
  persistedWarehouse: string | undefined,
): ShopSelection | null => {
  const store =
    (persistedStoreId ? stores.find(candidate => String(candidate.id) === String(persistedStoreId)) : undefined) ??
    (persistedWarehouse ? stores.find(candidate => candidate.coloradepotid === persistedWarehouse) : undefined);

  return store ? { id: store.id, coloradepotid: store.coloradepotid, name: store.name } : null;
};

/** The account's favourite resolved to a `ShopSelection` (null when unset or not in the stores list). */
const resolveFavouriteShop = (stores: types.Store[], favouriteShopId: string | undefined): ShopSelection | null => {
  const store = favouriteShopId ? stores.find(candidate => candidate.id === favouriteShopId) : undefined;

  return store ? { id: store.id, coloradepotid: store.coloradepotid, name: store.name } : null;
};

const mapPickupStores = (pointOfSaleData: Parameters<typeof handleStores>[0] | undefined): types.Store[] =>
  pointOfSaleData ? handleStores([...pointOfSaleData], storeConfig) : [];

type MutableRef<T> = { current: T };
type BasketSelection = { method: DeliveryMethod; shop: ShopSelection | undefined };
type BasketFlags = { userTouched: boolean; pending: boolean };
type DeliveryState = { queues: Map<string, DeliveryWriteQueue>; session: BasketSession };

const resolveBasketForCurrentId = (basket: IBasket | undefined, basketId: string | undefined): IBasket | undefined =>
  basketId && basket?.id && basket.id !== basketId ? undefined : basket;

const translationOr = (value: string | null | undefined, fallback: string): string => value ?? fallback;

const resolveBasketStockContext = ({
  accountData,
  stores,
  basket,
  basketId,
}: {
  accountData: ReturnType<typeof useAccount>['data'];
  stores: types.Store[];
  basket: IBasket | undefined;
  basketId: string | undefined;
}) => {
  const favouriteShopId = accountData?.preference?.shop;
  const basketForCurrentId = resolveBasketForCurrentId(basket, basketId);
  const persistedShop = resolvePersistedShop(
    stores,
    basketForCurrentId?.delivery?.storeid,
    basketForCurrentId?.delivery?.warehouse,
  );

  return {
    favouriteShopId,
    preferredDelivery: accountData?.preference?.delivery,
    preferredShop: resolveFavouriteShop(stores, favouriteShopId),
    persistedDeliveryMethod: readPersistedDeliveryMethod(basketForCurrentId),
    persistedShop,
    basketLoaded: basketForCurrentId !== undefined,
  };
};

/** Persisted selection wins over the account favourite; terminal once applied (user changes win). */
const syncBasketSelectionSession = ({
  session,
  basketId,
  flags,
  selectionRef,
  setDeliveryMethod,
  setSelectedShop,
  persistedDeliveryMethod,
  preferredDelivery,
  preferredShop,
  persistedShop,
}: {
  session: BasketSession;
  basketId: string | undefined;
  flags: MutableRef<BasketFlags>;
  selectionRef: MutableRef<BasketSelection>;
  setDeliveryMethod: (method: DeliveryMethod) => void;
  setSelectedShop: (shop: ShopSelection | undefined) => void;
  persistedDeliveryMethod: number | undefined;
  preferredDelivery: DeliveryMethod | null | undefined;
  preferredShop: ShopSelection | null;
  persistedShop: ShopSelection | null;
}) => {
  if (!shouldResetBasketSelection(session, basketId)) {
    return;
  }

  flags.current = { userTouched: false, pending: false };
  selectionRef.current = {
    method: getInitialDeliveryMethod(persistedDeliveryMethod, preferredDelivery, preferredShop),
    shop: getInitialShop(persistedShop, persistedDeliveryMethod, preferredShop),
  };
  setDeliveryMethod(selectionRef.current.method);
  setSelectedShop(selectionRef.current.shop);
};

// Build skus from basket lines AS-IS (no pre-sum/conversion — backend handles cumulation per ticket 44891).
// The stock API resolves by `stockproductnumber` (same identifier the PDP and checkout stock checks send).
const buildStockSkus = (basketLines: IBasketLine[]): StockSku[] =>
  (basketLines ?? []).map(line => ({
    skuid: line.item.stockproductnumber ?? '',
    quantity: line.quantity.salesquantity,
    unit: line.quantity.salesunit,
  }));

// One request includes HM and every pickup warehouse, regardless of the selected method.
const isStockQueryEnabled = (storesLoaded: boolean, skuCount: number): boolean => storesLoaded && skuCount > 0;

const deriveStockView = ({
  stockData,
  warehouseIds,
  storeWarehouseIds,
  stockSkus,
  stockTranslations,
  deliveryMethod,
  selectedShop,
}: {
  stockData: IArticleStock | undefined;
  warehouseIds: string[];
  storeWarehouseIds: string[];
  stockSkus: StockSku[];
  stockTranslations: StockStatusLabelTranslations;
  deliveryMethod: DeliveryMethod;
  selectedShop: ShopSelection | undefined;
}) => {
  const stockLinesByWarehouse = buildWarehouseStockLines(stockData, warehouseIds, stockSkus, stockTranslations);
  const selectedWarehouse = getSelectedWarehouse(deliveryMethod, selectedShop);

  return {
    storeStockMap: buildStoreStockMap(storeWarehouseIds, stockLinesByWarehouse),
    methodSummaries: getMethodSummaries(stockLinesByWarehouse, selectedShop),
    stockBySkuid: buildStockBySkuid(selectedWarehouse ? stockLinesByWarehouse.get(selectedWarehouse) : undefined),
  };
};

export const useBasketStock = ({
  locale,
  basketLines,
  basket,
  basketId,
  patchBasket,
}: UseBasketStockArgs): UseBasketStockResult => {
  const { t } = useTranslation('product');
  const { data: accountData } = useAccount(locale);
  const { data: pointOfSaleData, isLoading: storesLoading } = usePointsOfSales(3);
  // Match usePointOfSale's transformation, but compute synchronously from query data so
  // the stock request starts with the complete warehouse list rather than HM alone first.
  const stores = mapPickupStores(pointOfSaleData);

  const { favouriteShopId, preferredDelivery, preferredShop, persistedDeliveryMethod, persistedShop, basketLoaded } =
    resolveBasketStockContext({ accountData, stores, basket, basketId });

  const stockTranslations: StockStatusLabelTranslations = {
    available: translationOr(t('productPanel.stock.availability.available'), 'Op voorraad'),
    insufficient: translationOr(t('productPanel.stock.availability.insufficient'), 'Onvoldoende'),
    unavailable: translationOr(t('productPanel.stock.availability.unavailable'), 'Niet op voorraad'),
  };

  const storeWarehouseIds = stores.map(store => store.coloradepotid).filter(Boolean);
  const warehouseIds = Array.from(new Set(['HM', ...storeWarehouseIds]));
  const storesLoaded = !storesLoading;
  const canPersistDelivery = Boolean(basketId && patchBasket);

  // Initialization priority: basket-persisted choice > account favourite > 'Delivery'
  // (synchronous so a refresh does not flicker through the account default).
  const [deliveryMethod, _setDeliveryMethod] = useState<DeliveryMethod>(() =>
    getInitialDeliveryMethod(persistedDeliveryMethod, preferredDelivery, preferredShop),
  );
  const [selectedShop, _setSelectedShop] = useState<ShopSelection | undefined>(() =>
    getInitialShop(persistedShop, persistedDeliveryMethod, preferredShop),
  );
  const selectionRef = useRef<BasketSelection>({ method: deliveryMethod, shop: selectedShop });
  // Set when the customer changes the choice themselves — automatic preselection never overrides it.
  const flagsRef = useRef<BasketFlags>({ userTouched: false, pending: false });
  // Serialize this hook's delivery writes per basket so late responses cannot overwrite a newer choice.
  const deliveryStateRef = useRef<DeliveryState>({
    queues: new Map(),
    session: { basketId, generation: 0, lastBasketId: basketId },
  });

  syncBasketSelectionSession({
    session: deliveryStateRef.current.session,
    basketId,
    flags: flagsRef,
    selectionRef,
    setDeliveryMethod: _setDeliveryMethod,
    setSelectedShop: _setSelectedShop,
    persistedDeliveryMethod,
    preferredDelivery,
    preferredShop,
    persistedShop,
  });

  const basketSessionGeneration = deliveryStateRef.current.session.generation;

  const persistDelivery = useCallback(
    (method: DeliveryMethod, shop: ShopSelection | undefined) =>
      persistBasketDelivery(deliveryStateRef.current.queues, basketId, patchBasket, method, shop),
    [basketId, patchBasket],
  );

  const waitForDeliveryPersistence = () =>
    waitForBasketDeliveryPersistence(
      deliveryStateRef.current.queues,
      basketId,
      patchBasket,
      flagsRef.current.pending,
      deliveryStateRef.current.session,
      basketSessionGeneration,
    );

  const setDeliveryMethod = (method: DeliveryMethod) => {
    selectionRef.current.method = method;
    _setDeliveryMethod(method);
    flagsRef.current = { userTouched: true, pending: !canPersistDelivery };
    persistDelivery(method, selectionRef.current.shop);
  };

  const setSelectedShop = (shop: ShopSelection | undefined) => {
    selectionRef.current.shop = shop;
    _setSelectedShop(shop);
    flagsRef.current = { userTouched: true, pending: !canPersistDelivery };
    persistDelivery(selectionRef.current.method, shop);
  };

  useEffect(() => {
    persistPendingBasketSelection(basketId, patchBasket, flagsRef.current.pending, () => {
      flagsRef.current.pending = false;
      persistDelivery(selectionRef.current.method, selectionRef.current.shop);
    });
  }, [basketId, patchBasket, persistDelivery]);

  // ponytail: prefill when the choice becomes available — basket-persisted > account favourite.
  // The basket loads async on refresh, so persisted values are also applied once they arrive.
  // A resolvable persisted store is terminal; an unresolvable one heals via the favourite prefill
  // once the stores list has loaded (persisting over the stale reference). User changes win.
  useEffect(() => {
    applyBasketPrefill({
      userTouched: flagsRef.current.userTouched,
      prefill: resolveBasketPrefill({
        persistedDeliveryMethod,
        persistedShop,
        selectedShop,
        storesLoaded,
        basketLoaded,
        preferredShop,
        preferredDelivery,
      }),
      selectedShop,
      setDeliveryMethod: method => {
        selectionRef.current.method = method;
        _setDeliveryMethod(method);
      },
      setSelectedShop: shop => {
        selectionRef.current.shop = shop;
        _setSelectedShop(shop);
      },
      currentDeliveryMethod: selectionRef.current.method,
      persistDelivery,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    basketLoaded,
    basketId,
    persistedDeliveryMethod,
    persistedShop?.id,
    preferredDelivery,
    preferredShop?.id,
    storesLoaded,
  ]);

  const stockSkus = buildStockSkus(basketLines);
  const stockQuery = useStockByWarehouseIds(
    { warehouseIds, skus: stockSkus },
    isStockQueryEnabled(storesLoaded, stockSkus.length),
  );

  const { storeStockMap, methodSummaries, stockBySkuid } = deriveStockView({
    stockData: stockQuery.data,
    warehouseIds,
    storeWarehouseIds,
    stockSkus,
    stockTranslations,
    deliveryMethod,
    selectedShop,
  });

  // Issue 2: block checkout when Pickup is selected but no store is chosen
  const isPickupWithoutShop = hasPickupWithoutShop(deliveryMethod, selectedShop);

  return {
    deliveryMethod,
    setDeliveryMethod,
    selectedShop,
    setSelectedShop,
    isPickupWithoutShop,
    stores,
    favouriteShopId,
    methodSummaries,
    isStockLoading: stockQuery.isLoading,
    stockBySkuid,
    storeStockMap,
    isStoreStockLoading: stockQuery.isLoading,
    waitForDeliveryPersistence,
  };
};
